<template>
	<v-dialog v-model="dialog" max-width="600">
		<template #activator="{ props }">
			<v-btn
				v-bind="props"
				id="logout-button"
				class="bg-secondary"
				elevation="2"
			>
				<span class="logout mr-1">Log out</span>

				<v-icon size="small"> mdi-logout-variant </v-icon>
			</v-btn>
		</template>

		<v-card class="pb-5" :disabled="!!loading" :loading="loading">
			<v-toolbar color="primary" dark flat>
				<v-toolbar-title>Log out</v-toolbar-title>

				<v-spacer />

				<v-tooltip location="bottom">
					<template #activator="{ props }">
						<v-btn v-bind="props" icon size="large" @click="close">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</template>

					<span>Close</span>
				</v-tooltip>
			</v-toolbar>

			<v-card-text
				v-if="dirty"
				class="pa-10 pb-0 mb-n5 text-center text-button text-error"
			>
				{{ dirtyText }}
			</v-card-text>

			<v-card-text class="pa-10 text-center text-subtitle-1">
				{{ confirmationText }}
			</v-card-text>

			<v-card-actions>
				<v-spacer />

				<v-btn class="bg-secondary" elevation="2" @click="logout">
					Log out
				</v-btn>

				<v-btn elevation="2" @click="close"> Cancel </v-btn>

				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import * as Sentry from "@sentry/vue";
import auth from "@/auth";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import tabManager from "@/utils/tabManager";
import CookieUtils from "@/utils/cookies";
import { abortAllInflight } from "@/utils/inflightRequests";

export default {
	name: "VLogout",
	emits: ["deactivate-activity-timer"],

	data() {
		return {
			confirmationText: "Are you sure you want to log out?",
			dirtyText: "Warning: All unsaved changes will be lost.",
			dialog: false,
			loading: false,
			notification: {
				message: "",
				status: "",
			},
		};
	},

	computed: {
		dirty() {
			return useMainStore().dirty;
		},
	},

	methods: {
		...mapActions(useMainStore, [
			"reset",
			"setAuthenticated",
			"showNotification",
		]),
		close: function () {
			this.dialog = false;
		},
		async logout() {
			this.loading = "secondary";

			try {
				// clear auto-login so user must authenticate again
				CookieUtils.clearAutoLogin();
			} catch (e) {
				/* ignore cookie errors */
			}

			// Close any open windows managed by the tab manager
			tabManager.closeAll();

			// deactivate activity tracker
			this.$emit("deactivate-activity-timer");

			// Abort still-running requests first: the session lives in a cookie,
			// so a response landing after the logout response would re-set the
			// session and auth cookies and log the user straight back in.
			abortAllInflight();

			try {
				const response = await this.$http.post("/users/logout");

				// close dialog
				this.close();

				// display success
				this.showNotification(response.data);
			} catch (error) {
				// display error
				if (error && error.response) {
					if (error.response.data.message) {
						this.showNotification(error.response.data);
					} else {
						this.showNotification(
							error.response.data.error ?? "Failed to log out",
						);
					}
				} else {
					// no response (offline): the local session still ends below
					console.error("Logout request failed:", error);
				}
			} finally {
				this.loading = false;
				// clear sentry user
				Sentry.setUser(null);
				// Reset LaunchDarkly to anonymous
				if (this.$ldClient) {
					this.$ldClient.identify({
						kind: "user",
						anonymous: true,
						key: "anonymous",
					});
				}
				// clear store + cookie so all three auth artifacts (backend
				// session attempt above + cookie + Pinia store) drop on every
				// logout, even if the logout POST rejected. Matches the already-
				// atomic App.vue autoLogout(). (DCTL-2100)
				this.reset();
				auth.clearAuthenticated();
				this.setAuthenticated(false);
				// hard navigation: a router push can be cancelled by a guard and
				// leave the old page rendering with a reset store (DCTL-2654)
				auth.leaveToLogin();
			}
		},
	},
};
</script>

<style scoped lang="scss">
.logout {
	line-height: 36px;
}
</style>
